import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Chip,
  OutlinedInput,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';
import { intoleranceOptions, dietaryPreferenceOptions } from '../../../utils/entityOptions';

/**
 * Formulaire de creation d'un Client (Coach)
 */
const AddClient = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: '',
    weight: '',
    height: '',
    gender: 'male',
    goals: '',
    dietaryPreferences: [],
    intolerances: [],
    medicalConditions: '',
    sportProfile: {
      nap: 1.5,
      period: 'base',
    },
  });

  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName) {
      errors.firstName = 'Prenom requis';
    }

    if (!formData.lastName) {
      errors.lastName = 'Nom requis';
    }

    if (!formData.email) {
      errors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }

    if (formData.age && (formData.age < 1 || formData.age > 120)) {
      errors.age = 'Age invalide';
    }

    if (formData.weight && (formData.weight < 1 || formData.weight > 500)) {
      errors.weight = 'Poids invalide';
    }

    if (formData.height && (formData.height < 50 || formData.height > 300)) {
      errors.height = 'Taille invalide';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle nested sportProfile fields
    if (name.startsWith('sportProfile.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        sportProfile: {
          ...prev.sportProfile,
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Prepare data
      const submitData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        medicalConditions: formData.medicalConditions
          ? formData.medicalConditions.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };

      await api.post('/coach/clients', submitData);

      setSuccess('Client ajoute avec succes !');

      // Redirect after short delay
      setTimeout(() => {
        navigate('/coach/clients');
      }, 1500);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Nouveau client - Coach Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/coach/clients')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h1">
            Nouveau client
          </Typography>
        </Stack>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* Section: Informations personnelles */}
                <Typography variant="h3">
                  Informations personnelles
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Prenom"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Nom"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                    required
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Telephone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+33 6 12 34 56 78"
                  />
                </Stack>

                <FormControl fullWidth>
                  <InputLabel>Genre</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Genre"
                  >
                    <MenuItem value="male">Masculin</MenuItem>
                    <MenuItem value="female">Feminin</MenuItem>
                    <MenuItem value="other">Autre</MenuItem>
                  </Select>
                </FormControl>

                <Divider />

                {/* Section: Donnees physiques */}
                <Typography variant="h3">
                  Donnees physiques
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleChange}
                    error={!!formErrors.age}
                    helperText={formErrors.age}
                    inputProps={{ min: 1, max: 120 }}
                  />
                  <TextField
                    fullWidth
                    label="Poids (kg)"
                    name="weight"
                    type="number"
                    value={formData.weight}
                    onChange={handleChange}
                    error={!!formErrors.weight}
                    helperText={formErrors.weight}
                    inputProps={{ min: 1, step: 0.1 }}
                  />
                  <TextField
                    fullWidth
                    label="Taille (cm)"
                    name="height"
                    type="number"
                    value={formData.height}
                    onChange={handleChange}
                    error={!!formErrors.height}
                    helperText={formErrors.height}
                    inputProps={{ min: 50, max: 300 }}
                  />
                </Stack>

                <Divider />

                {/* Section: Profil sportif */}
                <Typography variant="h3">
                  Profil sportif
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Niveau d'activite (NAP)</InputLabel>
                    <Select
                      name="sportProfile.nap"
                      value={formData.sportProfile.nap}
                      onChange={handleChange}
                      label="Niveau d'activite (NAP)"
                    >
                      <MenuItem value={1.3}>Sedentaire / 1-2 seances par semaine</MenuItem>
                      <MenuItem value={1.5}>Quotidien dynamique / 3-5 seances par semaine</MenuItem>
                      <MenuItem value={1.7}>Travail physique / 5+ seances par semaine</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Periode d'entrainement</InputLabel>
                    <Select
                      name="sportProfile.period"
                      value={formData.sportProfile.period}
                      onChange={handleChange}
                      label="Periode d'entrainement"
                    >
                      <MenuItem value="base">Preparation / Base</MenuItem>
                      <MenuItem value="intensification">Intensification</MenuItem>
                      <MenuItem value="competition">Competition</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Divider />

                {/* Section: Objectifs et sante */}
                <Typography variant="h3">
                  Objectifs et sante
                </Typography>

                <TextField
                  fullWidth
                  label="Objectifs"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Perte de poids, prise de masse, performance..."
                />

                <TextField
                  fullWidth
                  label="Conditions medicales"
                  name="medicalConditions"
                  value={formData.medicalConditions}
                  onChange={handleChange}
                  placeholder="Separez par des virgules"
                  helperText="Ex: Diabete, Hypertension"
                />

                <Divider />

                {/* Section: Regimes et intolerances */}
                <Typography variant="h3">
                  Regimes et intolerances
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Intolerances alimentaires</InputLabel>
                  <Select
                    name="intolerances"
                    multiple
                    value={formData.intolerances}
                    onChange={handleChange}
                    input={<OutlinedInput label="Intolerances alimentaires" />}
                    renderValue={(selected) => (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={intoleranceOptions.find(o => o.value === value)?.label || value}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  >
                    {intoleranceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Preferences alimentaires</InputLabel>
                  <Select
                    name="dietaryPreferences"
                    multiple
                    value={formData.dietaryPreferences}
                    onChange={handleChange}
                    input={<OutlinedInput label="Preferences alimentaires" />}
                    renderValue={(selected) => (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={dietaryPreferenceOptions.find(o => o.value === value)?.label || value}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  >
                    {dietaryPreferenceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider />

                {/* Actions */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/coach/clients')}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    Creer le client
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default AddClient;
